import { GraphNode, GraphEdge, GraphData } from '../types/graph';
import type { CloudSaasCorrelation, CloudEntityBlastRadius } from '../services/cloudApi';

function findActivityNode(
  activityNodes: GraphNode[],
  entityEmail: string
): GraphNode | undefined {
  return activityNodes.find(
    (n) =>
      (n.type === 'user' || n.type === 'service_account') &&
      (n.props?.email === entityEmail ||
        n.props?.serviceAccountEmail === entityEmail ||
        n.label === entityEmail ||
        n.id === `user-${entityEmail}` ||
        n.id === `sa-${entityEmail}`)
  );
}

function cloneEntityNode(
  activityNode: GraphNode,
  modePrefix: string,
  extraProps: Record<string, any>
): GraphNode {
  return {
    ...activityNode,
    id: `${modePrefix}-entity-${activityNode.id}`,
    props: {
      ...activityNode.props,
      ...extraProps,

      originalNodeId: activityNode.id,
      originalGraphId: activityNode.props?.graphId,
    },
  };
}

function createFallbackEntityNode(
  entityEmail: string,
  modePrefix: string,
  extraProps: Record<string, any>
): GraphNode {
  return {
    id: `${modePrefix}-entity-${entityEmail}`,
    type: 'user',
    label: entityEmail,
    props: {
      email: entityEmail,
      ...extraProps,
    },
  };
}

export function transformCorrelationsToGraph(
  correlations: CloudSaasCorrelation[],
  activityNodes: GraphNode[]
): GraphData {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const nodeIds = new Set<string>();
  let edgeId = 0;

  correlations.forEach((corr) => {
    const correlationMap = corr.correlation || {};

    Object.entries(correlationMap).forEach(([entity, secretMap]) => {
      // Find matching activity node for this entity
      const activityNode = findActivityNode(activityNodes, entity);
      const entityId = activityNode
        ? `corr-entity-${activityNode.id}`
        : `corr-entity-${entity}`;

      if (!nodeIds.has(entityId)) {
        nodeIds.add(entityId);

        const modeProps = {
          isCorrelationMode: true,
          correlationInfo: corr.info,
          severity: corr.severity,
          status: corr.status,
          correlationId: corr.id,
          saasType: corr.info?.saasType,
        };

        if (activityNode) {
          nodes.push(cloneEntityNode(activityNode, 'corr', modeProps));
        } else {
          nodes.push(
            createFallbackEntityNode(entity, 'corr', {
              ...modeProps,
              cloudProvider: corr.cloudType || 'GCP',
              cloudType: corr.cloudType || 'GCP',
            })
          );
        }
      }

      Object.entries(secretMap).forEach(([secretName, saasResources]) => {
        // Create secret node
        const secretId = `corr-secret-${corr.id}-${secretName}`;
        if (!nodeIds.has(secretId)) {
          nodeIds.add(secretId);
          nodes.push({
            id: secretId,
            type: 'secret',
            label: secretName,
            props: {
              isCorrelationMode: true,
              correlationInfo: corr.info,
              severity: corr.severity,
              correlationId: corr.id,
            },
          });
        }

        // Edge: entity → secret
        edges.push({
          id: `corr-edge-${edgeId++}`,
          source: entityId,
          target: secretId,
          type: 'uses',
        });

        // Create SaaS app node
        const saasType = corr.info?.saasType || 'GITHUB';
        const saasAppId = `corr-saas-${corr.id}-${saasType}`;
        if (!nodeIds.has(saasAppId)) {
          nodeIds.add(saasAppId);
          nodes.push({
            id: saasAppId,
            type: 'token',
            label: saasType,
            props: {
              isCorrelationMode: true,
              saasType,
              correlationInfo: corr.info,
              severity: corr.severity,
              correlationId: corr.id,
            },
          });
        }

        // Edge: secret → saas app
        edges.push({
          id: `corr-edge-${edgeId++}`,
          source: secretId,
          target: saasAppId,
          type: 'uses',
        });

        // Create SaaS resource nodes (repos)
        (saasResources || []).forEach((resource) => {
          const resourceId = `corr-resource-${corr.id}-${resource}`;
          if (!nodeIds.has(resourceId)) {
            nodeIds.add(resourceId);
            nodes.push({
              id: resourceId,
              type: 'resource',
              label: resource,
              props: {
                isCorrelationMode: true,
                resourceType: 'repository',
                saasType,
                correlationInfo: corr.info,
                severity: corr.severity,
                correlationId: corr.id,
              },
            });
          }

          // Edge: saas app → resource
          edges.push({
            id: `corr-edge-${edgeId++}`,
            source: saasAppId,
            target: resourceId,
            type: 'acts_on',
          });
        });
      });
    });
  });

  return { nodes, edges, stats: { users: nodes.filter((n) => n.type === 'user' || n.type === 'service_account').length } };
}

/**
 * Transform blast radius API data into graph nodes and edges.
 * Reuses actual activity entity nodes (same cards, counts, activity bars).
 * Hierarchy: entity (user/sa) → cloud secret → SaaS app → SaaS resource
 */
export function transformBlastRadiusToGraph(
  blastRadiusData: CloudEntityBlastRadius[],
  activityNodes: GraphNode[]
): GraphData {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const nodeIds = new Set<string>();
  let edgeId = 0;

  blastRadiusData.forEach((br) => {
    const blastMap = br.blastRadius || {};

    Object.entries(blastMap).forEach(([entity, secretMap]) => {
      // Find matching activity node for this entity
      const activityNode = findActivityNode(activityNodes, entity);
      const entityId = activityNode
        ? `blast-entity-${activityNode.id}`
        : `blast-entity-${entity}`;

      if (!nodeIds.has(entityId)) {
        nodeIds.add(entityId);

        const modeProps = {
          isBlastRadiusMode: true,
          blastRadiusInfo: br.info,
          blastRadiusId: br.id,
          severity: br.info?.risk?.severity,
        };

        if (activityNode) {
          nodes.push(cloneEntityNode(activityNode, 'blast', modeProps));
        } else {
          nodes.push(
            createFallbackEntityNode(entity, 'blast', {
              ...modeProps,
              cloudProvider: 'GCP',
              cloudType: 'GCP',
            })
          );
        }
      }

      Object.entries(secretMap).forEach(([secretName, saasResources]) => {
        // Create secret node
        const secretId = `blast-secret-${br.id}-${secretName}`;
        if (!nodeIds.has(secretId)) {
          nodeIds.add(secretId);
          nodes.push({
            id: secretId,
            type: 'secret',
            label: secretName,
            props: {
              isBlastRadiusMode: true,
              blastRadiusInfo: br.info,
              blastRadiusId: br.id,
            },
          });
        }

        // Edge: entity → secret
        edges.push({
          id: `blast-edge-${edgeId++}`,
          source: entityId,
          target: secretId,
          type: 'uses',
        });

        // Create SaaS app node
        const saasType = br.info?.reachableSaas?.saasType || 'GITHUB';
        const saasAppId = `blast-saas-${br.id}-${saasType}`;
        if (!nodeIds.has(saasAppId)) {
          nodeIds.add(saasAppId);
          nodes.push({
            id: saasAppId,
            type: 'token',
            label: saasType,
            props: {
              isBlastRadiusMode: true,
              saasType,
              blastRadiusInfo: br.info,
              blastRadiusId: br.id,
            },
          });
        }

        // Edge: secret → saas app
        edges.push({
          id: `blast-edge-${edgeId++}`,
          source: secretId,
          target: saasAppId,
          type: 'uses',
        });

        // Create SaaS resource nodes
        (saasResources || []).forEach((resource) => {
          const resourceId = `blast-resource-${br.id}-${resource}`;
          if (!nodeIds.has(resourceId)) {
            nodeIds.add(resourceId);
            nodes.push({
              id: resourceId,
              type: 'resource',
              label: resource,
              props: {
                isBlastRadiusMode: true,
                resourceType: 'repository',
                saasType,
                blastRadiusInfo: br.info,
                blastRadiusId: br.id,
              },
            });
          }

          // Edge: saas app → resource
          edges.push({
            id: `blast-edge-${edgeId++}`,
            source: saasAppId,
            target: resourceId,
            type: 'acts_on',
          });
        });
      });
    });
  });

  return { nodes, edges, stats: { users: nodes.filter((n) => n.type === 'user' || n.type === 'service_account').length } };
}
